// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Edit, Trash2, Plane, Hotel, Car, Fuel } from "lucide-react";
import { useUpcomingEventsForTravel, useTravelBookings, useAllTravelBookings, useEventStaffForTravel } from "@/hooks/useTravelData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const statusColors: Record<string, string> = {
  needed: "bg-red-500 text-white",
  requested: "bg-yellow-500 text-white",
  booked: "bg-blue-500 text-white",
  confirmed: "bg-green-500 text-white",
  cancelled: "bg-gray-400 text-white",
};

const typeIcons: Record<string, React.ReactNode> = {
  flight: <Plane className="h-3.5 w-3.5" />,
  hotel: <Hotel className="h-3.5 w-3.5" />,
  rental_car: <Car className="h-3.5 w-3.5" />,
  gas: <Fuel className="h-3.5 w-3.5" />,
};

const typeColors: Record<string, string> = {
  flight: "bg-blue-100 text-blue-800",
  hotel: "bg-purple-100 text-purple-800",
  rental_car: "bg-green-100 text-green-800",
  gas: "bg-orange-100 text-orange-800",
  rideshare: "bg-yellow-100 text-yellow-800",
  per_diem: "bg-teal-100 text-teal-800",
  other: "bg-gray-100 text-gray-800",
};

const emptyForm = { event_id: "", staff_id: "", travel_type: "flight", provider: "", departure_city: "", arrival_city: "", departure_date: "", return_date: "", confirmation_number: "", cost: "", booking_status: "needed", payment_method: "company_card", receipt_url: "", notes: "" };

const BookingsTab: React.FC = () => {
  const { data: events } = useUpcomingEventsForTravel();
  const { data: staffList } = useEventStaffForTravel();
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string>("all");
  const { data: eventBookings, isLoading: ebLoading } = useTravelBookings(selectedEventId === "all" ? null : selectedEventId);
  const { data: allBookings, isLoading: abLoading } = useAllTravelBookings();

  const bookings = selectedEventId === "all" ? allBookings : eventBookings;
  const isLoading = selectedEventId === "all" ? abLoading : ebLoading;

  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const filtered = useMemo(() => {
    return (bookings || []).filter((b: any) => {
      if (filterType !== "all" && b.travel_type !== filterType) return false;
      if (filterStatus !== "all" && b.booking_status !== filterStatus) return false;
      return true;
    });
  }, [bookings, filterType, filterStatus]);

  const handleOpen = (booking?: any) => {
    if (booking) {
      setEditId(booking.id);
      setForm({
        event_id: booking.event_id || "",
        staff_id: booking.staff_id || "",
        travel_type: booking.travel_type || "flight",
        provider: booking.provider || "",
        departure_city: booking.departure_city || "",
        arrival_city: booking.arrival_city || "",
        departure_date: booking.departure_date || "",
        return_date: booking.return_date || "",
        confirmation_number: booking.confirmation_number || "",
        cost: booking.cost?.toString() || "",
        booking_status: booking.booking_status || "needed",
        payment_method: booking.payment_method || "company_card",
        receipt_url: booking.receipt_url || "",
        notes: booking.notes || "",
      });
    } else {
      setEditId(null);
      setForm({ ...emptyForm, event_id: selectedEventId !== "all" ? selectedEventId : "" });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.event_id || !form.staff_id) { toast.error("Event and staff are required"); return; }
    const payload = {
      event_id: form.event_id,
      staff_id: form.staff_id,
      travel_type: form.travel_type,
      provider: form.provider || null,
      departure_city: form.departure_city || null,
      arrival_city: form.arrival_city || null,
      departure_date: form.departure_date || null,
      return_date: form.return_date || null,
      confirmation_number: form.confirmation_number || null,
      cost: form.cost ? parseFloat(form.cost) : null,
      booking_status: form.booking_status,
      payment_method: form.payment_method || null,
      receipt_url: form.receipt_url || null,
      notes: form.notes || null,
    };
    if (editId) {
      const { error } = await supabase.from("manage_travel_bookings").update(payload).eq("id", editId);
      if (error) { toast.error("Failed to update booking"); return; }
      toast.success("Booking updated");
    } else {
      const { error } = await supabase.from("manage_travel_bookings").insert(payload);
      if (error) { toast.error("Failed to create booking"); return; }
      toast.success("Booking created");
    }
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["travel-bookings"] });
    queryClient.invalidateQueries({ queryKey: ["travel-bookings-all"] });
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status"] });
    queryClient.invalidateQueries({ queryKey: ["travel-summary"] });
  };

  const handleStatusChange = async (id: string, status: string) => {
    const { error } = await supabase.from("manage_travel_bookings").update({ booking_status: status }).eq("id", id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(`Status updated to ${status}`);
    queryClient.invalidateQueries({ queryKey: ["travel-bookings"] });
    queryClient.invalidateQueries({ queryKey: ["travel-bookings-all"] });
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status"] });
    queryClient.invalidateQueries({ queryKey: ["travel-summary"] });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this booking?")) return;
    const { error } = await supabase.from("manage_travel_bookings").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Booking deleted");
    queryClient.invalidateQueries({ queryKey: ["travel-bookings"] });
    queryClient.invalidateQueries({ queryKey: ["travel-bookings-all"] });
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status"] });
    queryClient.invalidateQueries({ queryKey: ["travel-summary"] });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[280px]">
          <Label>Event</Label>
          <Select value={selectedEventId} onValueChange={setSelectedEventId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {(events || []).map((e: any) => (
                <SelectItem key={e.id} value={e.id}>{e.name} ({format(parseLocalDate(e.start_date), "MMM d")})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="flight">Flight</SelectItem>
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="rental_car">Rental Car</SelectItem>
              <SelectItem value="gas">Gas</SelectItem>
              <SelectItem value="rideshare">Rideshare</SelectItem>
              <SelectItem value="per_diem">Per Diem</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="needed">Needed</SelectItem>
              <SelectItem value="requested">Requested</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={() => handleOpen()}><Plus className="h-4 w-4 mr-1" />Add Booking</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editId ? "Edit Booking" : "Add Booking"}</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label>Event *</Label>
                <Select value={form.event_id} onValueChange={(v) => setForm({ ...form, event_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select event..." /></SelectTrigger>
                  <SelectContent>{(events || []).map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Staff Member *</Label>
                <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                  <SelectContent>{(staffList || []).map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Travel Type</Label>
                <Select value={form.travel_type} onValueChange={(v) => setForm({ ...form, travel_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flight">Flight</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="rental_car">Rental Car</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                    <SelectItem value="rideshare">Rideshare</SelectItem>
                    <SelectItem value="per_diem">Per Diem</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="e.g. Delta, Marriott" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>From City</Label><Input value={form.departure_city} onChange={(e) => setForm({ ...form, departure_city: e.target.value })} /></div>
                <div><Label>To City</Label><Input value={form.arrival_city} onChange={(e) => setForm({ ...form, arrival_city: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Departure</Label><Input type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} /></div>
                <div><Label>Return</Label><Input type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} /></div>
              </div>
              <div><Label>Confirmation #</Label><Input value={form.confirmation_number} onChange={(e) => setForm({ ...form, confirmation_number: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
                <div><Label>Payment</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_card">Company Card</SelectItem>
                      <SelectItem value="reimbursement">Reimbursement</SelectItem>
                      <SelectItem value="comped">Comped</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Receipt URL</Label><Input value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button onClick={handleSave} className="w-full">{editId ? "Update" : "Add"} Booking</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Conf #</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No bookings found.</TableCell></TableRow>
                  ) : filtered.map((b: any) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.staff?.name || "—"}</TableCell>
                      <TableCell>
                        <Badge className={typeColors[b.travel_type] || typeColors.other}>
                          <span className="flex items-center gap-1">{typeIcons[b.travel_type] || null}{b.travel_type?.replace("_", " ")}</span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{b.provider || "—"}</TableCell>
                      <TableCell className="text-sm">{b.departure_city && b.arrival_city ? `${b.departure_city} → ${b.arrival_city}` : b.departure_city || b.arrival_city || "—"}</TableCell>
                      <TableCell className="text-sm">{b.departure_date ? format(new Date(b.departure_date), "MMM d") : "—"}{b.return_date ? ` – ${format(new Date(b.return_date), "MMM d")}` : ""}</TableCell>
                      <TableCell className="text-sm font-mono">{b.confirmation_number || "—"}</TableCell>
                      <TableCell className="text-right">{b.cost ? `$${Number(b.cost).toLocaleString()}` : "—"}</TableCell>
                      <TableCell>
                        <Select value={b.booking_status} onValueChange={(v) => handleStatusChange(b.id, v)}>
                          <SelectTrigger className="w-[120px]">
                            <Badge className={statusColors[b.booking_status] || ""}>{b.booking_status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="needed">Needed</SelectItem>
                            <SelectItem value="requested">Requested</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm">{b.payment_method?.replace("_", " ") || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => handleOpen(b)}><Edit className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(b.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BookingsTab;
